import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Box, IconButton, Typography, Paper } from '@mui/material';
import FeatherIcon from 'feather-icons-react';

const columnStyles = {
  name: { width: '40px !important' },
  modelNo: { width: '40px !important' },
  requestsSupported: { width: '40px !important' },
  quantity: { width: '40px !important' },
  costPerUnit: { width: '40px !important' },
  totalCost: { width: '40px !important' },
  locationDetails: { width: '40px !important' },
  status: { width: '40px !important' },
  timestamps: { width: '40px !important' },
  actions: { width: '40px !important', textAlign: 'center' },
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const FacilityItemsTable = ({ items, onEdit, onDelete }) => {
  const columns = [
    {
      dataField: 'name',
      text: 'Item Name',
      sort: true,
      headerStyle: () => columnStyles.name,
      style: columnStyles.name,
    },
    {
      dataField: 'modelNo',
      text: 'Model Number',
      headerStyle: () => columnStyles.modelNo,
      style: columnStyles.modelNo,
      formatter: (cell) => cell || 'N/A',
    },
    {
      dataField: 'requestsSupported',
      text: 'Request Types',
      sort: true,
      headerStyle: () => columnStyles.requestsSupported,
      style: columnStyles.requestsSupported,
      formatter: (cell) => {
        if (!cell || !Array.isArray(cell)) return 'N/A';
        return cell.map(type => type?.request_type || 'N/A').join(', ') || 'N/A';
      },
    },
    {
      dataField: 'quantity',
      text: 'Quantity',
      sort: true,
      headerStyle: () => columnStyles.quantity,
      style: columnStyles.quantity,
      formatter: (cell) => cell ?? 0,
    },
    {
      dataField: 'costPerUnit',
      text: 'Cost Per Unit',
      sort: true,
      headerStyle: () => columnStyles.costPerUnit,
      style: columnStyles.costPerUnit,
      formatter: (cell) =>
        cell ? `$${Number(cell).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'N/A',
    },
    {
      dataField: 'totalCost',
      text: 'Total Cost',
      sort: true,
      headerStyle: () => columnStyles.totalCost,
      style: columnStyles.totalCost,
      formatter: (cell, row) => {
        const quantity = row.quantity ?? 0;
        const costPerUnit = row.costPerUnit;

        if (!costPerUnit || quantity === 0) return 'N/A';

        const totalCost = Number(costPerUnit) * Number(quantity);
        return `$${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
      },
    },
    {
      dataField: 'locationDetails',
      text: 'Location',
      headerStyle: () => columnStyles.locationDetails,
      style: columnStyles.locationDetails,
      formatter: (cell) => {
        if (!cell) return 'N/A';
        const { room, notes } = cell;
        return [room, notes].filter(Boolean).join(' / ') || 'N/A';
      },
    },
    {
      dataField: 'status',
      text: 'Status',
      sort: true,
      headerStyle: () => columnStyles.status,
      style: columnStyles.status,
      formatter: (cell) => {
        const statusMap = {
          active: 'Active',
          inactive: 'Inactive',
          under_maintenance: 'Under Maintenance',
          retired: 'Retired',
        };
        return statusMap[cell] || cell || 'N/A';
      },
    },
    {
      dataField: 'updatedAt',
      text: 'Created / Updated',
      sort: true,
      sortFunc: (a, b, order) => {
        const diff = new Date(a || 0) - new Date(b || 0);
        return order === 'asc' ? diff : -diff;
      },
      headerStyle: () => columnStyles.timestamps,
      style: columnStyles.timestamps,
      formatter: (cell, row) => {
        const created = formatDate(row.createdAt);
        const updated = formatDate(row.updatedAt);
        if (!created && !updated) return 'N/A';
        return (
          <div style={{ lineHeight: 1.3 }} title={`Created ${created || 'N/A'} · Updated ${updated || 'N/A'}`}>
            <div>{created || 'N/A'}</div>
            {updated && updated !== created && (
              <small className="text-muted">Updated {updated}</small>
            )}
          </div>
        );
      },
    },
    {
      dataField: 'actions',
      text: 'Actions',
      headerStyle: () => columnStyles.actions,
      style: columnStyles.actions,
      formatter: (cell, row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <IconButton onClick={() => onEdit(row)} title="Edit" size="small">
            <FeatherIcon icon="edit" size={18} />
          </IconButton>
          <IconButton onClick={() => onDelete(row)} title="Delete" size="small">
            <FeatherIcon icon="trash-2" size={18} color="red" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const paginationOptions = {
    sizePerPage: 10,
    hideSizePerPage: true,
    hidePageListOnlyOnePage: true,
  };

  const NoDataIndication = () => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="45vh" width="100%">
      <Box textAlign="center" py={4}>
        <FeatherIcon icon="inbox" size={40} style={{ opacity: 0.5, marginBottom: '16px' }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          There are no facility inventory items to display
        </Typography>
      </Box>
    </Box>
  );

  const tableStyles = {
    minHeight: '50vh',
    maxHeight: '70vh',
    overflow: 'auto',
    borderRadius: 1,
    boxShadow: 1,
    position: 'relative',
    zIndex: 1,
    '& .react-bootstrap-table': {
      minHeight: '45vh'
    },
    '& table': {
      borderCollapse: 'separate',
      borderSpacing: 0,
      tableLayout: 'fixed',
      width: '100%'
    },
    '& thead th': {
      position: 'sticky',
      top: 0,
      backgroundColor: '#f8f9fa',
      zIndex: 2,
      borderBottom: '2px solid #dee2e6',
      padding: '12px 8px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& tbody td': {
      padding: '10px 8px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& tbody tr:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
    },
    '& .pagination': {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '16px',
      borderTop: '1px solid #dee2e6',
    }
  };

  return (
    <Paper sx={{
      ...tableStyles,
      '& thead th:nth-of-type(1)': {
        width: '100px !important',
      },
      '& thead th:nth-of-type(2)': {
        width: '100px !important',
      },
      '& thead th:nth-of-type(3)': {
        width: '130px !important',
      },
      '& thead th:nth-of-type(4)': {
        width: '70px !important',
      },
      '& thead th:nth-of-type(5)': {
        width: '90px !important',
      },
      '& thead th:nth-of-type(6)': {
        width: '100px !important',
      },
      '& thead th:nth-of-type(7)': {
        width: '260px !important',
      },
      '& thead th:nth-of-type(8)': {
        width: '80px !important',
      },
      '& thead th:nth-of-type(9)': {
        width: '130px !important',
      },
      '& thead th:nth-of-type(10)': {
        width: '120px !important',
      }
    }} className="miui-table scrollable-table">
      <BootstrapTable
        keyField="_id"
        data={items || []}
        columns={columns}
        pagination={paginationFactory(paginationOptions)}
        bootstrap4
        bordered={false}
        hover
        noDataIndication={NoDataIndication}
        wrapperClasses="table-responsive"
      />
    </Paper>
  );
};

export { FacilityItemsTable };